import jsQR from 'jsqr';
import {Box, Text} from 'native-base';
import React, {useEffect, useRef, useState} from 'react';
import {t} from 'react-native-tailwindcss';
import i18n from '../lib/i18n';

type Props = {
  onScan: (data: string) => void;
  paused?: boolean;
};

// expo-barcode-scanner has no web support: read camera frames and decode them with jsQR
export const QRScanner: React.FC<Props> = ({onScan, paused}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState(false);
  const onScanRef = useRef(onScan);
  const pausedRef = useRef(paused);
  onScanRef.current = onScan;
  pausedRef.current = paused;

  useEffect(() => {
    let stream: MediaStream | undefined;
    let frame: number | undefined;
    let stopped = false;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', {
      willReadFrequently: true
    }) as CanvasRenderingContext2D | null;

    const tick = () => {
      if (stopped) return;
      const video = videoRef.current;
      // Keep the loop alive whatever a frame or the scan handler throws
      try {
        if (
          context &&
          video &&
          !pausedRef.current &&
          video.readyState === video.HAVE_ENOUGH_DATA &&
          video.videoWidth > 0
        ) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const image = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(image.data, image.width, image.height, {
            inversionAttempts: 'dontInvert'
          });
          if (code?.data) onScanRef.current(code.data);
        }
      } catch (e) {
        console.warn(e);
      }
      frame = requestAnimationFrame(tick);
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      setError(true);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({video: {facingMode: 'environment'}, audio: false})
      .then((mediaStream) => {
        if (stopped) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = mediaStream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = mediaStream;
        void video.play();
        frame = requestAnimationFrame(tick);
      })
      .catch(() => setError(true));

    return () => {
      stopped = true;
      if (frame) cancelAnimationFrame(frame);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  if (error) {
    return (
      <Box style={[t.flex1, t.itemsCenter, t.justifyCenter, t.p6]}>
        <Text textAlign="center">{i18n.t('cameraPermissionDenied')}</Text>
      </Box>
    );
  }

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      style={{position: 'absolute', width: '100%', height: '100%', objectFit: 'cover'}}
    />
  );
};
